import { FC } from "react";
import { useNavigate } from "react-router-dom";
import usePagination from "../../../../hooks/usePagination";
import { useSearchFilter } from "../../../../hooks/useSearchFilter";
import { useSettingDefinitions } from "../../hooks/useSettingDefinitions";
import { SettingDefinition } from "../../models/SettingDefinition";

import {
  Table,
  Column,
} from "../../../../components/reusable-components/table/Table";
import ListHeader from "../../../../components/reusable-components/table/ListHeader";
import { useSelectFilter } from "../../../../hooks/useSelectFilter";
import EntityTypeFilterSelect from "../EntityTypeFilterSelect";
import { EntityType } from "../../models/enums/EntityType";

const SettingDefinitionsList: FC = () => {
  const navigate = useNavigate();

  const pagination = usePagination<keyof SettingDefinition>({
    initialPage: 1,
    initialSortBy: "id",
    initialSortDir: "desc",
  });

  const { value: selectedEntityType, handleChange: handleEntityTypeChange } =
    useSelectFilter<EntityType | "all">("all", () => pagination.goToPage(1));
  

  const { search, debouncedSearch, handleSearchChange } = useSearchFilter({
    onDebounce: () => pagination.goToPage(1),
  });

  const {
    fetch,
    pagination: { total },
  } = useSettingDefinitions({
    params: {
      ...pagination.query,
      search: debouncedSearch,
      entity_type:
        selectedEntityType === "all" ? undefined : selectedEntityType,
    },
  });

  const { data: definitions, loading, error } = fetch;

  const columns: Column<SettingDefinition>[] = [
    { field: "id", headerName: "ID", sortable: true },
    { field: "entity_type", headerName: "Entity Type" },
    { field: "key", headerName: "Key", sortable: true },
    { field: "type", headerName: "Type" },
    { field: "default_value", headerName: "Default Value" },
    { field: "description", headerName: "Description" },
  ];

  return (
    <div>
      <ListHeader
        title="Setting Definitions"
        search={{
          value: search,
          onChange: handleSearchChange,
          placeholder: "Search by key...",
          loading,
          disabled: loading,
        }}
        onCreate={() => navigate("/setting-definition/create")}
        createLabel="Add Setting"
      >
        <EntityTypeFilterSelect
          value={selectedEntityType}
          onChange={(val) => {
            handleEntityTypeChange(val);
            pagination.goToPage(1);
          }}
          includeAllOption
        />
      </ListHeader>

      <Table<SettingDefinition>
        data={definitions}
        columns={columns}
        loading={loading}
        error={error}
        bodyProps={{
          onEdit: (row) => navigate(`/setting-definition/edit/${row.id}`),
        }}
        sortBy={pagination.sortBy}
        sortDir={pagination.sortDir}
        onSortChange={pagination.handleSortChange}
        pagination={{
          page: pagination.currentPage - 1,
          rowsPerPage: 10,
          count: total,
          onPageChange: (_, newPage) => pagination.goToPage(newPage + 1),
          onRowsPerPageChange: () => {},
        }}
      />
    </div>
  );
};

export default SettingDefinitionsList;
